// ~/composables/useApi.ts
import { ofetch } from 'ofetch'
import pluralize from 'pluralize'
import { camelCase } from 'case-anything'

type SearchQuery<Query = { [key: string]: any }> = {
  $page?: number
  $limit?: number
} & Query

export interface IXMLHtppParams {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: FormData | string
  onProgress: (progress: number) => void
}

export const useApi = () => {
  const runtimeConfig = useRuntimeConfig()
  const auth = useAuth()

  const adonisApi = ofetch.create({
    baseURL: runtimeConfig.public.apiUrl,
    headers: { 'Content-Type': 'application/json' },
    async onRequest({ options }) {
      if (auth.isAuthenticated) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${auth.jwt}`,
        }
      }
    },
  })

  const adonisXMLApi = (
    url: string,
    { method, body, onProgress }: IXMLHtppParams,
  ) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.open(
        method,
        new URL(url, runtimeConfig.public.apiUrl).toString(),
        true,
      )

      if (auth.isAuthenticated) {
        xhr.setRequestHeader('Authorization', `Bearer ${auth.jwt}`)
      }

      xhr.upload.onprogress = (e) => {
        const progressPercentage = Math.round((e.loaded / e.total) * 100)
        onProgress(progressPercentage)
      }

      xhr.onreadystatechange = () => {
        const { status, response, readyState } = xhr
        if (readyState === 4) {
          resolve(response)
        }
      }

      xhr.onerror = (e) => {
        reject(e)
      }

      xhr.send(body)
    })
  }

  const makeCrudMethods = <S extends SearchQuery>(path: string) => ({
    find: (query?: S) => adonisApi(path, { params: query }),
    findOne: (query?: S) =>
      adonisApi(path, { params: query }).then(
        (res) => res.data?.[0] ?? res?.[0],
      ),
    create: (body: any) => adonisApi(path, { method: 'POST', body }),
    update: (id: string, body: any) =>
      adonisApi(`${path}/${id}`, { method: 'PATCH', body }),
    get: (id: string) => adonisApi(`${path}/${id}`),
    delete: (id: string) => adonisApi(`${path}/${id}`, { method: 'DELETE' }),
  })

  return {
    service(name: string) {
      return (
        this[camelCase(pluralize(name)) as keyof ReturnType<typeof useApi>] ??
        this[name as keyof ReturnType<typeof useApi>]
      )
    },

    resource(resourceRef: string) {
      const [name, id] = resourceRef.split(':')
      return this.service(name).get(id)
    },

    // --- EXISTING SERVICES ---
    users: {
      ...makeCrudMethods('/users'),
      me: () => adonisApi('/users/me'),
      find: () => adonisApi('/users/me'),
    },

    auth: {
      msalCallback: (body: { accessToken: string }) =>
        adonisApi('/msal-callback', { method: 'POST', body }),
    },

    projects: {
      ...makeCrudMethods('/projects'),
      getByCode: (code: string) => adonisApi(`/projects/code/${code}`),
      getProjectsCollaboratorByProjectManager: (collaboratorId: string) =>
        adonisApi(`/projects/collaborator/${collaboratorId}`) as Promise<any>,
      isReadyForDelete: (projectId: string) =>
        adonisApi(`/projects/is-ready-for-delete/${projectId}`),
      getStats: (projectId: string) =>
        adonisApi(`/projects/stats/${projectId}`),
    },

    tenants: {
      ...makeCrudMethods('/tenants'),
      updateSettings: (id: string, body: any) =>
        adonisApi(`/tenants/${id}/settings`, { method: 'PATCH', body }),
    },

    entities: {
      ...makeCrudMethods('/entities'),
      export: (ids: string[]) =>
        adonisApi(`/entities/export`, {
          method: 'POST',
          body: { ids },
          responseType: 'blob',
        }),
      import: (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return adonisApi('/entities/import', {
          method: 'POST',
          body: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      },
    },

    integrations: {
      ...makeCrudMethods('/integrations'),
      execute: (id: string, action: string, body: any) =>
        adonisApi(`/integrations/${id}/${action}`, { method: 'POST', body }),
    },

    currencies: makeCrudMethods('/currencies'),

    tenantSettings: makeCrudMethods('/tenant-settings'),

    public: {
      get: () => adonisApi('/public-config'),
    },

    roles: makeCrudMethods('/roles'),
    permissions: makeCrudMethods('/permissions'),
    userRoles: makeCrudMethods('/user-roles'),
    rolePermissions: makeCrudMethods('/role-permissions'),
    customers: makeCrudMethods('/customers'),
    rooms: makeCrudMethods('/rooms'),
    roomPrices: makeCrudMethods('/room-prices'),
    roomAmenities: {
      ...makeCrudMethods('/room-amenities'),
      findAll: () => adonisApi('/room-amenities/all'),
    },
    bookings: {
      ...makeCrudMethods('/bookings'),
      updateStatus: (id: string, body: { status: string }) =>
        adonisApi(`/bookings/${id}/status`, { method: 'PATCH', body }),
    },

    // Public endpoints (guest)
    publicRooms: {
      ...makeCrudMethods('/public/rooms'),
      availability: (roomId: string, q: { start: string; end: string }) =>
        adonisApi(`/public/rooms/${roomId}/availability`, { params: q }),
      calculatePrices: (
        roomId: string,
        q: { start: string; end: string; people?: number },
      ) => adonisApi(`/public/rooms/${roomId}/prices`, { params: q }),
    },
    publicTenants: {
      find: (query?: any) => adonisApi('/public/tenants', { params: query }),
      get: (id: string) => adonisApi(`/public/tenants/${id}`),
    },
    publicRoomPrices: {
      find: (query?: any) =>
        adonisApi('/public/room-prices', { params: query }),
    },
    publicCustomers: {
      getByEmail: (email: string) =>
        adonisApi(`/public/customers/by-email/${email}`),
    },
    publicBookings: {
      create: (body: any) =>
        adonisApi('/public/bookings', { method: 'POST', body }),
    },

    invoices: {
      ...makeCrudMethods('/invoices'),
      updateStatus: (id: string, status: string) =>
        adonisApi(`/invoices/${id}/status`, { method: 'PATCH', body: { status } }),
      preview: (id: string) => adonisApi(`/invoices/${id}/preview`),
    },

    notifications: {
      ...makeCrudMethods('/notifications'),
      markRead: (id: string) =>
        adonisApi(`/notifications/${id}/read`, { method: 'PATCH' }),
      markReadByBooking: (bookingId: string) =>
        adonisApi(`/notifications/by-booking/${bookingId}/read`, {
          method: 'PATCH',
        }),
      markAllRead: () =>
        adonisApi('/notifications/read-all', { method: 'PATCH' }),
    },

    dashboard: {
      summary: (q: { start: string; end: string }) =>
        adonisApi('/dashboard/summary', { params: q }),
      timeseries: (q: {
        start: string
        end: string
        period: 'daily' | 'weekly' | 'monthly'
      }) => adonisApi('/dashboard/timeseries', { params: q }),
      upcoming: (q: { limit?: number }) =>
        adonisApi('/dashboard/upcoming', { params: q }),
      newBookings: () => adonisApi('/dashboard/new-bookings'),
      agenda: () => adonisApi('/dashboard/agenda'),
    },
  }
}
